import type { ReturnTypeFromGetUser } from '@tyro/api';
import { useEffect } from 'react';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  type RouteObject,
  useLocation,
  useNavigationType,
} from 'react-router';

type SentryModule = typeof import('@sentry/react');

let sentryModule: SentryModule | null = null;

// Start loading Sentry immediately in production, but don't block anything
const sentryReady: Promise<SentryModule | null> =
  process.env.NODE_ENV === 'production'
    ? import('@sentry/react').then((mod) => {
        sentryModule = mod;
        return mod;
      })
    : Promise.resolve(null);

export function initSentry(dsn: string) {
  if (process.env.NODE_ENV !== 'production') return;

  sentryReady.then((Sentry) => {
    if (!Sentry) return;

    Sentry.init({
      dsn,
      integrations: [
        Sentry.reactRouterV7BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],
      ignoreErrors: [
        'Failed to fetch',
        'NetworkError',
        'Network request failed',
        'Load failed',
        'CancelledError',
        'ChunkLoadError',
        /Loading chunk \d+ failed/,
        'GraphQL Error (Code: 401)',
        'GraphQL Error (Code: 403)',
      ],
      beforeBreadcrumb(breadcrumb) {
        if (
          breadcrumb.category === 'fetch' &&
          typeof breadcrumb.data?.url === 'string' &&
          breadcrumb.data.url.endsWith('/api/tyrogql')
        ) {
          return null;
        }
        return breadcrumb;
      },
      tracePropagationTargets: [/^https:\/\/api\.tyro\.school\/api/],
      tracesSampleRate: 0.5,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      normalizeDepth: 11,
    });

    Sentry.lazyLoadIntegration('replayIntegration')
      .then((replayIntegration) => {
        Sentry.addIntegration(
          replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        );
      })
      .catch(() => {
        // Replay is non-critical — silently degrade if CDN bundle fails to load
      });
  });
}

export async function sentryCreateBrowserRouter(routes: RouteObject[]) {
  const Sentry = await sentryReady;
  if (Sentry) {
    return Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)(routes);
  }
  return createBrowserRouter(routes);
}

export function setSentryUser(
  activeProfile: ReturnTypeFromGetUser['activeProfile'],
) {
  if (activeProfile?.partyId && sentryModule) {
    sentryModule.setUser({
      id: String(activeProfile.partyId),
      segment: activeProfile.profileType?.userType ?? 'unknown',
      tenant: activeProfile.tenant?.tenant ?? 'unknown',
    });
  }
}
